import { useState, useCallback } from "react";
import { useAuth } from "../../components/context/LoginContext/AuthContext";

export const useAutoSwitchStudent = () => {
  const { fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ CHANGE 1: Accept optional token param
  const switchStudent = useCallback(
    async (studentId, forPreview = true, token = null) => {
      try {
        // ✅ Build headers manually if token provided, else fallback to fetchWithAuth
        let result;

        if (token) {
          // Use raw fetch with explicit token — context not needed
          const response = await fetch(`${apiUrl}/parent/student/switch`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              student_id: studentId,
              for_preview: forPreview,
            }),
          });
          result = await response.json();
        } else {
          // Fallback: use context-based fetchWithAuth (for non-login flows)
          const res = await fetchWithAuth(`${apiUrl}/parent/student/switch`, {
            method: "POST",
            body: JSON.stringify({
              student_id: studentId,
              for_preview: forPreview,
            }),
          });
          result = (await res.json?.()) ?? res;
        }

        if (!result.token)
          throw new Error(result.message || "Failed to switch student");

        return result;
      } catch (err) {
        throw err;
      }
    },
    [apiUrl, fetchWithAuth],
  );

  const fetchAndSwitchToFirstStudent = useCallback(
    async (dummy_required = false, backupToken = null) => {
      setLoading(true);
      setError(null);

      try {
        let targetToken = backupToken;

        if (!targetToken) {
          const parentLocalData = JSON.parse(
            localStorage.getItem("parentLocalData"),
          );
          targetToken = parentLocalData?.token;
        }

        if (!targetToken) {
          throw new Error("Parent authentication token not found.");
        }

        const response = await fetch(`${apiUrl}/parent/onboarding/students`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${targetToken}`,
          },
        });

        if (!response.ok)
          throw new Error("Failed to fetch onboarding students");

        const responseData = await response.json();
        let studentsList = responseData?.students || [];

        if (!dummy_required && studentsList.length > 1) {
          studentsList = studentsList.filter((student) => student.age != null);
        }

        if (studentsList.length === 0) {
          throw new Error("No student profiles available.");
        }

        const firstStudent = studentsList[0];

        if (!firstStudent?.id) {
          throw new Error("Student ID missing from top level array index.");
        }

        // ✅ CHANGE 2: Pass targetToken into switchStudent
        const switchResult = await switchStudent(
          firstStudent.id,
          true,
          targetToken,
        );
        return switchResult;
      } catch (err) {
        setError(err.message || "Error during auto switching");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, switchStudent],
  );

  return { switchStudent, fetchAndSwitchToFirstStudent, loading, error };
};
